/* This module exports shared functions used by both blueprint & template endpoints, as these REST APIs share similar
 * request/response patterns.
 */

// eslint-disable-next-line no-restricted-imports
import { BadStatusError } from '@confluence/network';
// Type imports are fine
// eslint-disable-next-line no-restricted-imports
import type { Cfetch } from '@confluence/network';
import { createEditDraftPageUrl } from '@confluence/content-utils/entry-points/newContentUrl.ts';

export const getOnContentUpdatedObjFromResponse = (
	json: any,
): {
	content: {
		id: any;
		type: string;
		subType: any;
		title: string | null;
		space: {
			key: any;
		};
		_links: {
			editui: any;
		};
		metadata: {
			properties: {
				editor: {
					value: any;
				};
			};
			simple: {
				isFabric: any;
			};
		};
	};
} => {
	const title = json?.content?.title;
	return {
		content: {
			id: json?.content?.id || null,
			type: 'page',
			subType: json?.content?.subType || null,
			title: typeof title === 'string' ? title : null,
			space: {
				key: json?.content?.space?.key || null,
			},
			_links: {
				editui: json?.content?._links?.editui || null,
			},
			metadata: {
				properties: {
					editor: {
						value: json?.content?.metadata?.properties?.editor?.value || null,
					},
				},
				simple: {
					isFabric: json?.content?.metadata?.simple?.isFabric || null,
				},
			},
		},
	};
};

export const updateContentWithRetry = async (
	cfetch: Cfetch,
	url: string,
	method: string,
	headers: RequestInit['headers'],
	pageData: any,
): Promise<{
	content: {
		id: any;
		type: string;
		subType: any;
		title: string | null;
		space: {
			key: any;
		};
		_links: {
			editui: any;
		};
		metadata: {
			properties: {
				editor: {
					value: any;
				};
			};
			simple: {
				isFabric: any;
			};
		};
	};
}> => {
	const MAX_CONTENT_UPDATE_RETRY = 1;

	const updateTemplate = async (count = 0): Promise<any> => {
		try {
			const res = await cfetch(url, {
				method,
				headers,
				body: JSON.stringify(pageData),
			});
			return res.json();
		} catch (err) {
			const errorStatus = err?.response?.status;
			if (errorStatus === 500) {
				const backendError = await err.response.json();
				throw new BadStatusError(backendError.errorMessage, err.response);
			}
			if (count < MAX_CONTENT_UPDATE_RETRY && errorStatus === 409) {
				return updateTemplate(++count);
			}
			throw err;
		}
	};

	const json = await updateTemplate();
	return getOnContentUpdatedObjFromResponse(json);
};

export const getEditDraftPageUrl = async (
	createPageResponseJson: any,
	attemptSpaUrl?: boolean | null,
): Promise<any> => {
	if (attemptSpaUrl) {
		// TODO: remove the old metadata field check once we use the new metadata field for template
		const isFabric =
			createPageResponseJson?.content?.metadata?.properties?.editor?.value === 'v2' ||
			createPageResponseJson?.content?.metadata?.simple?.isFabric === true;
		const isLivePage = createPageResponseJson?.content?.subType === 'live';

		if (isLivePage) {
			return createPageResponseJson?.content?._links?.editui;
		}

		if (isFabric) {
			return createEditDraftPageUrl(createPageResponseJson);
		}
	}

	return createPageResponseJson?.content?._links?.editui;
};
